// #region Imports
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import downloadFile from "_shared/_utilities/download-file";
import Guid from "_shared/guids/guid";
import DocumentAPIService from "documents/_api/document-api-service";
import DocumentSelector from "documents/_views/selectors/document-selector";
import DocumentUploadOverlay from "documents/_views/uploader/document-upload-overlay";
import Document from "documents/document";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useMemo, useState } from "react";
import { useSession } from "users/__session/session-context";
import { Masked } from "work/_shared/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import { IssueContainer } from "work/proposals/issues/_views/proposal-issue-container";
import ProposalIssues, { ProposalIssueLevel } from "work/proposals/issues/proposal-issues";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import SelectedDocumentList from "work/work-agreements/contracts/_views/selected-document-list";
import { WorkDocumentType } from "work/work-agreements/contracts/types";
import WorkDocument from "work/work-agreements/contracts/work-document";
import { xor } from "lodash";
import ResizablePanel from "_shared/_views/resizeable-panel/resizable-panel";
// #endregion

// #region Styled Components
const ListContainer = styled(Grid)(() => ({
  flexWrap: "nowrap",
  height: "100%",
  minHeight: "22rem",
  width: "100%",
}));
const DocumentSelectorContainer = styled(ResizablePanel)(() => ({
  display: "flex",
  position: "relative"
}));
const SelectedDocsList = styled(SelectedDocumentList)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    marginTop: theme.spacing(2),
  },
  maxHeight: "60vh",
  marginLeft: theme.spacing(2),
  minWidth: 0,
  overflowY: "auto",
  position: "relative",
  flex: 1,
  "& .MuiListItem-root": {
    maxWidth: "550px",
  }
}));
const HeadControls = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "end",
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2)
}));
const Uploader = styled(DocumentUploadOverlay)(() => ({
  backgroundColor: "rgba(250, 250, 250, 0.5)",
  backdropFilter: "blur(5px) saturate(200%)",
  position: "absolute",
  top: 0,
  left: 0,
  margin: "1rem",
  width: "calc(100% - 2rem)",
  height: "calc(100% - 2rem)",
  zIndex: 9999,
}));
// #endregion

type PolicyUploadProps = {
  activeReviewField?: ProposalField;
  proposalBuilder: ProposalBuilder;
  issues?: ProposalIssues;
  isSaved: boolean;
  commentsToolTip: string;
  commentThreads?: CommentThread[];
  disableEditing?: boolean;
  onCommentsClicked?: (field: ProposalField, name?: string) => void;
  onProposalBuilderUpdated: (updatedProposalBuilder: ProposalBuilder) => void;
};

export default function PoliciesTab(props: Readonly<PolicyUploadProps>) {
  const {
    proposalBuilder,
    commentsToolTip,
    commentThreads,
    isSaved,
    disableEditing,
    activeReviewField,
    onCommentsClicked,
    onProposalBuilderUpdated,
  } = props;

  // #region State
  const [creatingDocumentFromTemplate, setCreatingDocumentFromTemplate] = useState<Guid | null>(null);
  const [isDownloadingFile, setIsDownloadingFile] = useState<Guid | null>(null);

  // #region Hooks
  const confirm = useConfirmDialog();
  const session = useSession();

  // #region Memoized Values
  const issues = useMemo(
    () =>
      props.issues?.entries.filter((issue) => {
        const candidates = [
          ProposalField.Policies,
          ProposalField.VendorPolicies,
          ProposalField.ClientPolicies,
          ProposalField.VendorPolicy(issue.field.id ?? Guid.EMPTY),
          ProposalField.ClientPolicy(issue.field.id ?? Guid.EMPTY),
        ];

        return candidates.some((field) => field.isEqualTo(issue.field));
      }),
    [props.issues?.entries]
  );

  const hasActiveReviewFieldIssue = useMemo(() => {
    return issues?.some((issue) => issue.field.isEqualTo(activeReviewField)) ?? false;
  }, [issues, activeReviewField]);

  const selectedDocuments = useMemo(() => {
    if (session.context?.viewingAsVendor) {
      if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement.vendorPolicies)) return [];
      return proposalBuilder.currentSpec.workAgreement.vendorPolicies ?? [];
    } else {
      if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement.clientPolicies)) return [];
      return proposalBuilder.currentSpec.workAgreement.clientPolicies ?? [];
    }
  }, [
    proposalBuilder.currentSpec.workAgreement.vendorPolicies,
    proposalBuilder.currentSpec.workAgreement.clientPolicies,
    session.context?.viewingAsVendor
  ]);

  const selectedDocumentIds = useMemo(
    () => selectedDocuments.map((doc) => doc.id),
    [selectedDocuments]
  );

  const selectedTemplateIds = useMemo(
    () => selectedDocuments.flatMap((doc) => doc.templateIds),
    [selectedDocuments]
  );
  // #endregion

  // #region Functions
  const handlePolicyDocumentRemoved = useCallback(async (documentId: Guid): Promise<void> => {
    if (
      Masked.isMasked(proposalBuilder.currentSpec.workAgreement.vendorPolicies) ||
      Masked.isMasked(proposalBuilder.currentSpec.workAgreement.clientPolicies)
    ) {
      throw new Error("You are not permitted to view contracts.");
    }
    const response = await confirm({
      title: "Remove Policy Document",
      message: "Are you sure you want to remove this policy document?",
    });

    if (response === ConfirmResponse.Cancel) return;

    let updatedProposalBuilder: ProposalBuilder;
    if (session.context?.viewingAsVendor) {
      updatedProposalBuilder = proposalBuilder.setVendorPolicies(
        [...(proposalBuilder.currentSpec.workAgreement.vendorPolicies ?? [])].filter(
          (doc) => !doc.id.isEqualTo(documentId)
        )
      );
    } else {
      updatedProposalBuilder = proposalBuilder.setClientPolicies(
        [...(proposalBuilder.currentSpec.workAgreement.clientPolicies ?? [])].filter(
          (doc) => !doc.id.isEqualTo(documentId)
        )
      );
    }

    onProposalBuilderUpdated(updatedProposalBuilder);
  }, [confirm, onProposalBuilderUpdated, proposalBuilder, session.context?.viewingAsVendor]);

  const handleDownloadDocumentById = useCallback(
    async (documentId: Guid, event?: React.MouseEvent<HTMLButtonElement, MouseEvent>): Promise<void> => {
      event?.stopPropagation();
      try {
        setIsDownloadingFile(documentId);
        const documentApiService = new DocumentAPIService(session);
        const policyDocument = await documentApiService.downloadDocument(documentId);
        await downloadFile(policyDocument);
      } catch (err) {
        console.error(err);
        enqueueSnackbar("Failed to download policy document. Please try again", {
          variant: "error",
        });
      } finally {
        setIsDownloadingFile(null);
      }
    }, [session]);

  const handleDocumentSelectionChanged = useCallback((selectedDocs: Document[], deselectedDocs: Document[]) => {
    if (
      Masked.isMasked(proposalBuilder.currentSpec.workAgreement.vendorPolicies) ||
      Masked.isMasked(proposalBuilder.currentSpec.workAgreement.clientPolicies)
    ) {
      throw new Error("You are not permitted to view contracts.");
    }

    let existingDocuments = session.context?.viewingAsVendor
      ? [...(proposalBuilder.currentSpec.workAgreement.vendorPolicies ?? [])]
      : [...(proposalBuilder.currentSpec.workAgreement.clientPolicies ?? [])];

    let currentDocuments = [...existingDocuments];

    for (const selectedDoc of selectedDocs) {
      if (!currentDocuments.some((doc) => doc.id.isEqualTo(selectedDoc.id))) {
        currentDocuments.push(
          WorkDocument.fromDocument(
            selectedDoc,
            session.context?.viewingAsVendor ? WorkDocumentType.VendorPolicy : WorkDocumentType.ClientPolicy
          )
        );
      }
    }

    for (const deselectedDoc of deselectedDocs) {
      currentDocuments = currentDocuments.filter((doc) => !doc.id.isEqualTo(deselectedDoc.id));
    }

    // If the document lists are the same, do not trigger an update
    const existingDocIds = existingDocuments.map(d => d.id.value);
    const currentDocIds = currentDocuments.map(d => d.id.value);

    if (xor(existingDocIds, currentDocIds).length === 0) return;

    const updatedProposalBuilder = session.context?.viewingAsVendor
      ? proposalBuilder.setVendorPolicies(currentDocuments)
      : proposalBuilder.setClientPolicies(currentDocuments);

    onProposalBuilderUpdated(updatedProposalBuilder);
  }, [onProposalBuilderUpdated, proposalBuilder, session.context?.viewingAsVendor]);

  const handlePolicyDocumentAdded = useCallback((policyDocument: Document) => {
    if (
      Masked.isMasked(proposalBuilder.currentSpec.workAgreement.vendorPolicies) ||
      Masked.isMasked(proposalBuilder.currentSpec.workAgreement.clientPolicies)
    ) {
      throw new Error("You are not permitted to view contracts.");
    }

    let currentDocuments = session.context?.viewingAsVendor
      ? proposalBuilder.currentSpec.workAgreement.vendorPolicies ?? []
      : proposalBuilder.currentSpec.workAgreement.clientPolicies ?? [];
    currentDocuments = [...currentDocuments]; // Create a shallow copy

    const newDocument = WorkDocument.fromDocument(
      policyDocument,
      session.context?.viewingAsVendor ? WorkDocumentType.VendorPolicy : WorkDocumentType.ClientPolicy
    );
    currentDocuments.push(newDocument);

    let updatedProposalBuilder = proposalBuilder;
    if (session.context?.viewingAsVendor) {
      updatedProposalBuilder = updatedProposalBuilder.setVendorPolicies(currentDocuments);
    } else {
      updatedProposalBuilder = updatedProposalBuilder.setClientPolicies(currentDocuments);
    }

    setCreatingDocumentFromTemplate(null);
    onProposalBuilderUpdated(updatedProposalBuilder);
  }, [onProposalBuilderUpdated, proposalBuilder, session.context?.viewingAsVendor]);

  const handleBeginCreateDocumentFromTemplate = useCallback(async (templateId: Guid | undefined | null) => {
    if (!templateId) return;
    setCreatingDocumentFromTemplate(templateId);
    await handleDownloadDocumentById(templateId);
  }, [handleDownloadDocumentById]);
  // #endregion

  // #region Render
  if ((session.context?.viewingAsVendor && Masked.isMasked(proposalBuilder.currentSpec.workAgreement.vendorPolicies)) ||
    (!session.context?.viewingAsVendor && Masked.isMasked(proposalBuilder.currentSpec.workAgreement.clientPolicies))) {
    return (
      <Typography variant="body1">
        You do not have permission to view contracts
      </Typography>
    );
  }

  return (
    <>
      <HeadControls>
        {(issues?.length ?? 0) > 0 && (
          <IssueContainer active={hasActiveReviewFieldIssue}>
            {issues?.map((issue) => (
              <Typography
                key={issue.field?.cacheKey}
                color={issue.level === ProposalIssueLevel.Critical ? "error" : "warning"}
                variant="body1"
              >
                {issue.description}
              </Typography>
            ))}
          </IssueContainer>
        )}
      </HeadControls>
      <ListContainer container direction="row">
        {!disableEditing && (
          <>
            <DocumentSelectorContainer
              initialWidth={450}
              minWidth={375}
              minRemainingWidth={320}
            >
              <DocumentSelector
                documentType="policy"
                selectedDocumentIds={selectedDocumentIds}
                selectedTemplateIds={selectedTemplateIds}
                onDocumentSelectionChanged={handleDocumentSelectionChanged}
                onCreateDocumentFromTemplate={handleBeginCreateDocumentFromTemplate}
              />
            </DocumentSelectorContainer>
            <Grid>
              <Divider orientation="vertical" />
            </Grid>
          </>
        )}
        <SelectedDocsList
          documents={selectedDocuments}
          documentType={
            session.context?.viewingAsVendor ? WorkDocumentType.VendorPolicy : WorkDocumentType.ClientPolicy
          }
          downloadingFileId={isDownloadingFile}
          commentsToolTip={commentsToolTip}
          commentThreads={commentThreads}
          disableComments={!isSaved}
          disableEditing={disableEditing}
          isSaved={isSaved}
          activeReviewField={activeReviewField}
          onCommentsClicked={onCommentsClicked}
          onDownload={handleDownloadDocumentById}
          onDocumentRemoved={handlePolicyDocumentRemoved}
          onDocumentAdded={handlePolicyDocumentAdded}
        />
      </ListContainer>
      {creatingDocumentFromTemplate && (
        <Uploader
          session={session}
          documentType={
            session.context?.viewingAsVendor ? WorkDocumentType.VendorPolicy : WorkDocumentType.ClientPolicy
          }
          templateId={creatingDocumentFromTemplate}
          onDocumentUploaded={handlePolicyDocumentAdded}
          onClose={() => setCreatingDocumentFromTemplate(null)}
        />
      )}
    </>
  );
}
